import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import FormFooter from 'components/layout/form-footer';
import OWForm from 'components/layout/ow-form';
import { useTranslation } from 'react-i18next';
import LogoDropZone from 'components/controls/logo-drop-zone';
import { Company } from '../types/dtos';
import { ReactNode } from 'react';
import { ResponseCompanyOwnerDto } from 'features/company-owner/types/dtos';
import { FormProps } from 'types/form';
import { COUNTRIES } from '../constants/company-fields';
import { isEmpty } from 'utils/object-helpers';
import { ResponseCityDto } from 'features/city/types/dtos';

export type CompanyFormProps = FormProps<Company> & {
    companyOwners?: { content: ResponseCompanyOwnerDto[] };
    cities?: { content: ResponseCityDto[] };
};

type CompanyFormType = (props: CompanyFormProps) => ReactNode;

export const CompanyForm: CompanyFormType = ({
    formData: company,
    companyOwners,
    onChange,
    onSubmit,
    onCancel,
    isSaveLoading,
    disableSubmit,
    errors,
    cities,
}) => {
    const { t } = useTranslation();
    const filteredOwners = companyOwners?.content.filter(
        (owner) => owner.companyId === undefined || owner.companyId === company.id,
    );

    return (
        <OWForm onSubmit={onSubmit}>
            <Typography variant="h6">{t('COMPANY_FORM.HEADER')}</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Stack width="100%" spacing={1.25}>
                    <TextField
                        value={company.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder={t('COMPANY_FORM.PLACEHOLDER_NAME')}
                        label={t('COMPANY_FORM.LABEL_NAME')}
                        required
                        autoFocus
                        error={!!errors?.name}
                        helperText={errors?.name?.errorMessage}
                    />
                    <Autocomplete
                        options={filteredOwners || []}
                        value={
                            filteredOwners?.find((companyOwner) => companyOwner.id === company.companyOwnerId) || null
                        }
                        getOptionLabel={(option: { id: string; fullName: string }) => option.fullName}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('COMPANY_FORM.LABEL_COMPANY_OWNER')}
                                error={!!errors?.companyOwnerId}
                                helperText={errors?.companyOwnerId?.errorMessage}
                            />
                        )}
                        onChange={(_, value) => onChange('companyOwnerId', value?.id)}
                    />
                </Stack>
                <LogoDropZone logo={company.logo} onChange={(value) => onChange('logo', value)} />
            </Stack>
            <Divider variant="middle" />
            <Stack spacing={2}>
                <Autocomplete
                    options={Object.values(COUNTRIES)}
                    value={company.country}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            required
                            helperText={errors?.country?.errorMessage}
                            error={!!errors?.country}
                            label={t('COMPANY_FORM.LABEL_COUNTRY')}
                        />
                    )}
                    onChange={(_, value) => onChange('country', value)}
                />
                <Stack direction="row" spacing={1}>
                    <Autocomplete
                        options={cities?.content || []}
                        value={cities?.content.find((city) => city.id === company.cityId) || null}
                        getOptionLabel={(option: { id: string; name: string }) => option.name}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('PLACE_FORM.LABEL_CITY')}
                                required
                                helperText={errors?.cityId?.errorMessage}
                            />
                        )}
                        onChange={(_, value) => onChange('cityId', value?.id)}
                        fullWidth
                    />
                    <TextField
                        value={company.zipCode}
                        onChange={(e) => onChange('zipCode', e.target.value)}
                        placeholder={t('COMPANY_FORM.PLACEHOLDER_ZIP_CODE')}
                        label={t('COMPANY_FORM.LABEL_ZIP_CODE')}
                        required
                        error={!!errors?.zipCode}
                        helperText={errors?.zipCode?.errorMessage}
                        fullWidth
                    />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <TextField
                        value={company.street}
                        onChange={(e) => onChange('street', e.target.value)}
                        placeholder={t('COMPANY_FORM.PLACEHOLDER_STREET')}
                        label={t('COMPANY_FORM.LABEL_STREET')}
                        required
                        error={!!errors?.street}
                        helperText={errors?.street?.errorMessage}
                        fullWidth
                    />
                    <TextField
                        value={company.streetNumber}
                        onChange={(e) => onChange('streetNumber', e.target.value)}
                        placeholder={t('COMPANY_FORM.PLACEHOLDER_STREET_NUMBER')}
                        label={t('COMPANY_FORM.LABEL_STREET_NUMBER')}
                        required
                        error={!!errors?.streetNumber}
                        helperText={errors?.streetNumber?.errorMessage}
                        fullWidth
                    />
                </Stack>
                <Stack direction="row" spacing={1}>
                    <TextField
                        value={company.floor}
                        onChange={(e) => onChange('floor', e.target.value)}
                        placeholder={t('COMPANY_FORM.PLACEHOLDER_FLOOR')}
                        label={t('COMPANY_FORM.LABEL_FLOOR')}
                        error={!!errors?.floor}
                        helperText={errors?.floor?.errorMessage}
                        fullWidth
                    />
                    <TextField
                        value={company.apartment}
                        onChange={(e) => onChange('apartment', e.target.value)}
                        placeholder={t('COMPANY_FORM.PLACEHOLDER_APARTMENT')}
                        label={t('COMPANY_FORM.LABEL_APARTMENT')}
                        error={!!errors?.apartment}
                        helperText={errors?.apartment?.errorMessage}
                        fullWidth
                    />
                </Stack>
            </Stack>
            <Divider variant="middle" />
            <Stack spacing={2}>
                <TextField
                    value={company.vatNumber}
                    onChange={(e) => onChange('vatNumber', e.target.value)}
                    placeholder={t('COMPANY_FORM.PLACEHOLDER_VAT_NUMBER')}
                    label={t('COMPANY_FORM.LABEL_VAT_NUMBER')}
                    required
                    error={!!errors?.vatNumber}
                    helperText={errors?.vatNumber?.errorMessage}
                />
                <TextField
                    value={company.idNumber}
                    onChange={(e) => onChange('idNumber', e.target.value)}
                    placeholder={t('COMPANY_FORM.PLACEHOLDER_ID_NUMBER')}
                    label={t('COMPANY_FORM.LABEL_ID_NUMBER')}
                    required
                    error={!!errors?.idNumber}
                    helperText={errors?.idNumber?.errorMessage}
                />
            </Stack>
            <FormFooter>
                <Stack direction="row" width="360px" spacing={3}>
                    <Button variant="outlined" onClick={onCancel}>
                        {t('COMPANY_FORM.CANCEL')}
                    </Button>
                    <LoadingButton
                        loading={isSaveLoading}
                        type="submit"
                        disabled={!isEmpty(errors) || disableSubmit}
                        data-testid="submit-button"
                    >
                        {t('COMPANY_FORM.SUBMIT')}
                    </LoadingButton>
                </Stack>
            </FormFooter>
        </OWForm>
    );
};
